import * as React from 'react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  /**
   * This prop defines a label the element.
   */
  label?: string,
  /**
   * This prop defines a message beneath the textarea.
   */
  description?: string,
  /**
   * If not empty, the textarea will indicate an error.
   */
  error?: string,
  /**
   * Name attribute of the `textarea` element.
   */
  name: string,
  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
   */
  autoComplete?: 'on' | 'off',
  /**
   * If `true`, the `textarea` element will be focused during the first mount.
   */
  autoFocus?: boolean,
  /**
   * If `true`, the `textarea` element will be disabled.
   */
  disabled?: boolean,
  /**
   * If `true`, the textarea will take up the full width of its container.
   */
  fullWidth?: boolean,
  /**
   * Callback fired when the value is changed.
   */
  onChange: Function,
  /**
   * The short hint displayed in the textarea before the user enters a value.
   */
  placeholder?: string,
  /**
   * If `true`, the `textarea` element will be required.
   */
  required?: boolean,
  /**
   * This prop specifies a regular expression that the <textarea> element's value is checked against on form submission.
   */
  pattern?: string,
  /**
   * This prop describes the pattern to help user.
   */
  title?: string,
  /**
   * This prop specifies the maximum number of characters allowed in the <textarea> element.
   */
  maxLength?: string,
  /**
   * This prop defines assistive text to give user more context.
   */
  assistiveText?: string,
  /**
   * This prop defines mode specific styles for the component
   */
  mode?: 'Feedback',
  /**
   * This prop defines size of the element
   */
  size: 'large' | 'small' | 'xsmall',
  /**
   * The value of the `textarea` element, required for a controlled component.
   */
  value?: mixed,
};

const textareaStyles = {
  large: {
    label: {
      fontSize: theme => theme.fontSize.xl,
      fontWeight: theme => theme.fontWeight.bold,
    },
    textarea: {
      fontSize: theme => theme.fontSize.xxl,
      padding: '24px',
      'margin-bottom': '16px',
      'margin-top': '16px',
      height: '204px',
    },
    assistiveText: {
      fontSize: theme => theme.fontSize.xl,
    },
    error: {
      fontSize: theme => theme.fontSize.xl,
    },
  },

  small: {
    textarea: {
      fontSize: theme => theme.fontSize.lg,
      padding: '12px 16px',
      'margin-bottom': '8px',
      'margin-top': '8px',
      height: '144px',
    },
    label: {
      fontSize: theme => theme.fontSize.lg,
      fontWeight: theme => theme.fontWeight.regular,
    },
    assistiveText: {
      fontSize: theme => theme.fontSize.sm,
    },
    error: {
      fontSize: theme => theme.fontSize.sm,
    },
  },

  xsmall: {
    textarea: {
      fontSize: theme => theme.fontSize.md,
      padding: '12px 16px',
      'margin-bottom': '8px',
      'margin-top': '8px',
      height: '144px',
    },
    label: {
      fontSize: theme => theme.fontSize.md,
      fontWeight: theme => theme.fontWeight.regular,
    },
    assistiveText: {
      fontSize: theme => theme.fontSize.xs,
    },
    error: {
      fontSize: theme => theme.fontSize.xs,
    },
  },
};

export const Textarea = ({
  error,
  label,
  description,
  name,
  assistiveText,
  size,
  maxLength,
  value,
  mode,
  ...rest
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Textarea, mode);

  return (
    <React.Fragment>
      {label && (
        <FormLabel htmlFor={name} size={size}>
          {label}
        </FormLabel>
      )}
      <FormTextarea
        error={error}
        id={name}
        size={size}
        assistiveText={assistiveText}
        label={label}
        data-test={name}
        maxLength={maxLength}
        value={value}
        componentTheme={componentTheme}
        {...rest}
      />
      {maxLength && value && (
        <MaxLengthText size={size}>
          {value.length}/{maxLength}
        </MaxLengthText>
      )}
      {!error && assistiveText && <AssistiveText size={size}>{assistiveText}</AssistiveText>}
      {!error && description && <Description size={size}>{description}</Description>}
      {error && (
        <Error size={size} data-test={`${name}-validation-msg`}>
          {error}
        </Error>
      )}
    </React.Fragment>
  );
};

Textarea.defaultProps = {
  size: 'large',
};

const FormLabel = styled('label')`
  color: ${({ theme }) => theme.color.black};
  font-size: ${({ size, theme }) => textareaStyles[size].label.fontSize(theme)};
  font-weight: ${({ size, theme }) => textareaStyles[size].label.fontWeight(theme)};
`;

const FormTextarea = styled('textarea')`
  ${({ fullWidth }) => fullWidth && `width: 100%;`}
  ${({ size, error, assistiveText }) =>
    (error || assistiveText) && `margin-bottom: ${textareaStyles[size].textarea['margin-bottom']};`}
  ${({ size, label }) => label && `margin-top: ${textareaStyles[size].textarea['margin-top']};`}
  display: block;
  height: ${({ size }) => textareaStyles[size].textarea.height};
  background-color: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.black};
  font-size: ${({ size, theme }) => textareaStyles[size].textarea.fontSize(theme)};
  padding: ${({ size }) => textareaStyles[size].textarea.padding};
  border: 1px solid ${({ theme, error }) => (error ? theme.color.danger : theme.color.grey)};
  border-radius: ${({ theme, componentTheme }) => componentTheme.borderRadius || theme.borderRadius.medium};
  transition: all 0.25s ease;

  &:focus {
    outline: 0;
    border-color: ${({ theme }) => theme.color.info};
    box-shadow: ${({ theme }) => `0px 0px 8px ${transparentize(0.5, theme.color.info)}`};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.lightGrey};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.lightGrey};
    letter-spacing: normal;
  }
`;

const AssistiveText = styled('span')`
  color: ${({ theme }) => theme.color.grey};
  font-size: ${({ size, theme }) => textareaStyles[size].assistiveText.fontSize(theme)};
`;

const MaxLengthText = styled('p')`
  margin-top: 8px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.color.grey};
  font-size: ${({ size, theme }) => textareaStyles[size].assistiveText.fontSize(theme)};
`;

const Error = styled('span')`
  color: ${({ theme }) => theme.color.danger};
  font-size: ${({ size, theme }) => textareaStyles[size].error.fontSize(theme)};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const Description = styled('span')`
  display: block;
  font-size: ${({ size, theme }) => textareaStyles[size].error.fontSize(theme)};
  color: ${({ theme }) => theme.color.grey};
  margin-top: 16px;
`;
